import { useLoadScript } from "@react-google-maps/api";
import { useEffect, useMemo, useRef, useState } from "react";
import { useClientScope } from "@/components/pages/Client/settings/ClientScopeContext";
import { useGuestBooking } from "@/components/pages/Guest/GuestBookingContext";
import type { Place } from "@/hooks/usePlaces";
import { GOOGLE_MAPS_LOADER_OPTIONS } from "@/lib/googleMaps";
import { getPlacePredictionsForCountries } from "@/lib/googlePlaces";
import { snapToNearestRoad } from "@/lib/googleRoads";
import { useListClientPoisQuery, useListClientRecentPoisQuery } from "@/redux/apis/clientPoi/clientPoiApi";
import { PointsOfInterest, RM_TAGS } from "@/redux/apis/clientPoi/types";
import { useListProviderPoisQuery } from "@/redux/apis/providerPoi/providerPoiApi";
import { useAppSelector } from "@/redux/hooks";
import { bookableCountriesSelector, operatorIdSelector } from "@/redux/slices/operator/selectors";
import {
  LatLng,
  cleanQuery,
  dedupeGooglePredictions,
  filterPoisByCountry,
  googlePlaceToPoi,
  parseGoogleAddressComponents,
  rankLocalPois,
} from "../suggestions";

export const MIN_CHARS = 3;
const DEBOUNCE_MS = 1000;

const toPlace = (p: google.maps.places.AutocompletePrediction): Place => ({
  description: p.description,
  place_id: p.place_id,
  types: p.types,
  structured_formatting: {
    main_text: p.structured_formatting?.main_text ?? p.description,
    secondary_text: p.structured_formatting?.secondary_text ?? "",
  },
});

interface UseAddressSearchArgs {
  /** The text being searched. Below MIN_CHARS (after cleaning) both match lists are empty. */
  query: string;
  /** Gates the local-POI queries (recents/favourites/provider) — pass false until the user engages. */
  enabled: boolean;
  // When true, includes saved client favourites (Home/Work/Other) in suggestions.
  // Default false — the add-favourite flow excludes them to avoid self-referencing.
  includeFavourites?: boolean;
  // Optional proximity hint: biases Google predictions and boosts local ranking.
  userLocation?: LatLng | null;
  /**
   * The countries results may come from (lower-case ISO codes). Omit for the default — the provider's
   * bookable list (`bookableCountriesSelector`). `null` means no restriction at all, which the booking
   * sheet passes only for an operator booking on behalf whose provider has the Worldwide Address Search
   * add-on (see `useAddressSearchCountries`). Applied to Google predictions and to the local
   * favourites/recents/provider POIs alike. An empty list yields no Google predictions at all
   * (DEV-1606, DEV-1612).
   */
  countries?: string[] | null;
}

/**
 * Address search against local POIs (client recents/favourites + provider known POIs, ranked) and
 * debounced Google Places predictions (deduped against the local matches). The engine behind both
 * LocationAddressAutocomplete's dropdown and the booking sheet's shared results surface.
 *
 * Reuses the single @react-google-maps/api loader the Map atom uses (`GOOGLE_MAPS_LOADER_OPTIONS`), so
 * Google Maps JS loads only once — never add a second loader.
 */
export const useAddressSearch = ({ query, enabled, includeFavourites = false, userLocation, countries }: UseAddressSearchArgs) => {
  const [debounced, setDebounced] = useState("");
  const [predictions, setPredictions] = useState<Place[]>([]);

  const bookableCountries = useAppSelector(bookableCountriesSelector);
  // `undefined` = caller did not say → the provider's bookable list; `null` = caller asked for no restriction.
  const restriction = countries === undefined ? bookableCountries : countries;
  const { isLoaded } = useLoadScript(GOOGLE_MAPS_LOADER_OPTIONS);
  const servicesRef = useRef<{
    autocomplete: google.maps.places.AutocompleteService;
    places: google.maps.places.PlacesService;
  } | null>(null);

  useEffect(() => {
    if (isLoaded && !servicesRef.current && window.google?.maps?.places) {
      servicesRef.current = {
        autocomplete: new google.maps.places.AutocompleteService(),
        places: new google.maps.places.PlacesService(document.createElement("div")),
      };
    }
  }, [isLoaded]);

  // In operator scope, fetch the managed client's recents/favourites (not the operator's own).
  const operatorId = useAppSelector(operatorIdSelector);
  const { accountId } = useClientScope();
  const clientScopeArg = accountId ? { accountId } : undefined;
  const { isGuest } = useGuestBooking();
  const readsPoiLists = enabled && !isGuest;
  const { data: recent = [] } = useListClientRecentPoisQuery(clientScopeArg, { skip: !readsPoiLists });
  const { data: clientPois = [] } = useListClientPoisQuery(clientScopeArg, { skip: !readsPoiLists || !includeFavourites });
  const { data: providerPois = [] } = useListProviderPoisQuery(undefined, { skip: !readsPoiLists || !operatorId });
  // When includeFavourites=false (add-favourite flow): saved favourites are NOT a suggestion
  // source, and recents tagged Home/Work/Other are filtered out too.
  const filteredRecents = useMemo(
    () =>
      includeFavourites
        ? recent
        : recent.filter((poi) => ![RM_TAGS.Home, RM_TAGS.Work, RM_TAGS.Other].some((t) => poi.rm_tags?.includes(t))),
    [recent, includeFavourites]
  );

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  // `debounced` lags by up to DEBOUNCE_MS, so on a field switch or clear the previous query's
  // predictions would otherwise stay rendered (and pickable) under the new query — drop them the
  // moment the live query can no longer justify them.
  useEffect(() => {
    if (!enabled || cleanQuery(query).length < MIN_CHARS) setPredictions([]);
  }, [query, enabled]);

  useEffect(() => {
    const cleaned = cleanQuery(debounced);
    // `enabled` must gate this too: a mounted-but-unfocused consumer with a prefilled address
    // (edit-favourite modal) would otherwise fire real Places requests nothing ever renders.
    // No allowed countries (empty list) means no Google results; only an explicit `null` searches worldwide.
    if (!enabled || cleaned.length < MIN_CHARS || !servicesRef.current || (restriction !== null && restriction.length === 0)) {
      setPredictions([]);
      return;
    }
    const request: Omit<google.maps.places.AutocompletionRequest, "componentRestrictions"> = {
      // No `types` restriction — return all POI kinds (addresses, landmarks, establishments)
      // to mirror the mobile favourites flow (e.g. airports/landmarks are valid favourites).
      input: cleaned,
    };
    // Bias predictions toward the user's current location (mobile passes the same location bias),
    // so web and mobile surface the same nearby results.
    if (userLocation && window.google?.maps?.LatLng) {
      request.location = new google.maps.LatLng(userLocation.lat, userLocation.lng);
      request.radius = 50000;
    }
    // Google takes at most five countries per request; the helper fans out and merges (DEV-1612). The
    // fan-out is async, so a result landing after the query moved on must not overwrite the newer one.
    let stale = false;
    getPlacePredictionsForCountries(servicesRef.current.autocomplete, request, restriction).then((results) => {
      if (!stale) setPredictions(results.map(toPlace));
    });
    return () => {
      stale = true;
    };
  }, [debounced, restriction, userLocation, enabled]);

  // Saved places outside the allowed countries are not offered either — the same rule as the Google results.
  const localMatches = useMemo(
    () =>
      cleanQuery(query).length >= MIN_CHARS
        ? rankLocalPois(
            query,
            filterPoisByCountry([...clientPois, ...filteredRecents, ...providerPois], restriction),
            userLocation ?? undefined
          )
        : [],
    [query, clientPois, filteredRecents, providerPois, userLocation, restriction]
  );
  // Drop Google predictions that duplicate a shown local match, so the same place never appears
  // twice (vendor_uuid holds the originating place id for saved POIs).
  const remoteMatches = useMemo(() => {
    const localPlaceIds = localMatches.map((poi) => poi.vendor_uuid).filter(Boolean);
    return dedupeGooglePredictions(predictions, localPlaceIds);
  }, [predictions, localMatches]);

  /** Resolve a Google prediction to a full POI (details fetch + road snap); null when unavailable. */
  const resolvePlace = (place: Place): Promise<PointsOfInterest | null> =>
    new Promise((resolve) => {
      const places = servicesRef.current?.places;
      if (!places) return resolve(null);
      // Fetch address_components too — the backend requires city/state/postcode/country on a POI.
      places.getDetails(
        { placeId: place.place_id, fields: ["geometry.location", "address_components"] },
        async (details: google.maps.places.PlaceResult | null) => {
          const loc = details?.geometry?.location;
          if (!loc) return resolve(null);
          // Snap to the nearest road (mobile parity) before saving; falls back to the raw point.
          const snapped = await snapToNearestRoad({ lat: loc.lat(), lng: loc.lng() });
          const parts = parseGoogleAddressComponents(details?.address_components ?? []);
          resolve(googlePlaceToPoi(place, snapped, parts));
        }
      );
    });

  /** True while a searchable query's Google predictions haven't caught up yet (debounce lag). */
  const remotePending = enabled && cleanQuery(query).length >= MIN_CHARS && cleanQuery(debounced) !== cleanQuery(query);

  return { localMatches, remoteMatches, remotePending, resolvePlace };
};
